import { memo } from "react";

import { number } from "@euroland/format-number";
import { formatDateTime, InvestmentCalChart, mergeObject, replaceKey } from "../../utils";

import { POSITIVE_CHANGE_NUMBER_FORMAT, SERIES_COLUM_TYPE, TOOL_TEMPLATE } from "../../constant/common";
import { useTemplateSetting } from "../../hooks/useCompanySettings";
import i18n from "../../locale/i18n";
import { getCurrencyLabel } from "../../store/utils";
import SeriesNoteColumnChart from "../ui/SeriesNoteColumnChart";
import { useSelector } from "react-redux";
import { colorBlindModeSelector } from "../../store/slice/settings";
import { useRef } from "react";

import { HighChartWrapper, defaultOptions, tooltipOptions } from "@euroland/react-high-chart";
import Highcharts from "./setup";
/**
 * @typedef {import ('../../..').IAnnualClosePrice} IAnnualClosePrice
 */

const getTooltipValues = ({ tooltipOpts, formatNumber }) => {
  let value, change, changePer, sharePrice, dividendReinvested;
  const { currency } = tooltipOpts.points[0].series.options;
  const total = tooltipOpts.points.reduce((s, point) => s + point.y, 0);

  const noChangePoint = tooltipOpts.points.find(
    point => point.series.options.id === SERIES_COLUM_TYPE.OFFSET && point.y === total
  );

  const increasePoint = tooltipOpts.points.find(
    point => point.series.options.id === SERIES_COLUM_TYPE.INCREASE && !!point.y
  );
  const decreasePoint = tooltipOpts.points.find(
    point => point.series.options.id === SERIES_COLUM_TYPE.DECREASE && !!point.y
  );

  if (!!increasePoint) {
    sharePrice = formatNumber(increasePoint.series.options.sharePrice[tooltipOpts.x]);
    value = formatNumber(increasePoint.total);
    change = increasePoint.y;
    changePer = (increasePoint.y * 100) / (increasePoint.total - increasePoint.y);

    dividendReinvested =
      increasePoint.series.options.dividendData?.[tooltipOpts.x] &&
      formatNumber(increasePoint.series.options.dividendData[tooltipOpts.x]);
  } else if (!!decreasePoint) {
    sharePrice = formatNumber(decreasePoint.series.options.sharePrice[tooltipOpts.x]);
    value = formatNumber(decreasePoint.total - decreasePoint.y);
    change = -decreasePoint.y;
    changePer = -(decreasePoint.y * 100) / decreasePoint.total;
    dividendReinvested =
      decreasePoint.series.options.dividendData?.[tooltipOpts.x] &&
      formatNumber(decreasePoint.series.options.dividendData[tooltipOpts.x]);
  } else {
    //first point
    sharePrice = formatNumber(noChangePoint.series.options.sharePrice[tooltipOpts.x]);
    value = formatNumber(noChangePoint.total);
    dividendReinvested =
      noChangePoint.series.options.dividendData?.[tooltipOpts.x] &&
      formatNumber(noChangePoint.series.options.dividendData[tooltipOpts.x]);
  }
  return { value, change, changePer, sharePrice, dividendReinvested, currency };
};

export const getXAxisFormatByVarCss = (settings, periodType) => {
  const dateFormat = settings.chartVolume.formatXAxis;
  if (dateFormat === null) {
    return "DD/MM/YY";
  }
  switch (periodType) {
    case InvestmentCalChart.PeriodChartType.Day:
      return dateFormat.day;
    case InvestmentCalChart.PeriodChartType.Week:
      return dateFormat.week;
    case InvestmentCalChart.PeriodChartType.Month:
      return dateFormat.month;
    case InvestmentCalChart.PeriodChartType.Quarter:
      return dateFormat.quarter;
    case InvestmentCalChart.PeriodChartType.Year:
      return dateFormat.year;
    default:
      return "DD/MM/YY";
  }
};

/**
 *
 * @param {IAnnualClosePrice[]} data
 * @param { string } currencyCode
 * @returns { any }
 */
const createOptionsQuadrata =
  ({ data, currencyCode, categories, settings, periodType, dividendData, sharePrice, formatNumber }) =>
  chartUtils => {
    const { loadStyle, loadCommonStyle } = chartUtils;
    const series = data;
    // offset data = prev total data - decrease data (+ increase data);
    const increaseData = data[0].datas;
    const offsetData = data[2].datas;
    const minValue = Math.min(...offsetData.map((o, i) => o.value + increaseData[i].value));
    const xAxisLabelFormat = getXAxisFormatByVarCss(settings, periodType);

    const result = mergeObject(defaultOptions(chartUtils), {
      chart: {
        type: "column",
      },
      xAxis: {
        labels: {
          formatter: function () {
            return categories[this.value].format(xAxisLabelFormat);
          },
        },
      },
      yAxis: {
        title: {
          text: currencyCode,
        },
        labels: {
          formatter: function () {
            return formatNumber(this.value, { decimalDigits: 0 });
          },
        },
        stackLabels: {
          enabled: false,
        },
        min: parseInt(minValue * 0.9),
      },
      plotOptions: {
        column: {
          stacking: "normal",
          dataLabels: {
            enabled: false,
          },
          states: {
            hover: {
              brightness: loadStyle("--chart-column-hover-brightness", 0.1).toNumber()
            }
          }
        },
      },
      accessibility: {
        point: {
          descriptionFormatter: function(point) {
            const seriesData = point.series.chart.series.reduce((a, s) => {
              a[s.name] = s.options.data[point.x];
              return a;
            }, {
              TOTAL: point.series.chart.series[0].points.map(p => p.total)[point.x],
              SHARE_PRICE: point.series.chart.series[0].options.sharePrice[point.x],
              DIVIDEND_DATA: point.series.chart.series[0].options?.dividendData?.[point.x]
            });
            const value = seriesData.TOTAL - seriesData[SERIES_COLUM_TYPE.DECREASE];
            const change = seriesData[SERIES_COLUM_TYPE.INCREASE] - seriesData[SERIES_COLUM_TYPE.DECREASE];
            const changePer = (change * 100) / (seriesData.TOTAL - seriesData[SERIES_COLUM_TYPE.INCREASE]);

            return `${formatDateTime(categories[point.x], settings)}, ${
                replaceKey(i18n.translate("valueCurrencyLabel"), [currencyCode])
              } ${value}, ${seriesData.DIVIDEND_DATA? `${
                replaceKey(i18n.translate("dividendReturnCurrencyLabel"), [currencyCode])
              } ${seriesData.DIVIDEND_DATA}, `: ''}${
                replaceKey(i18n.translate("sharePriceCurrencyLabel"), [currencyCode])
              } ${seriesData.SHARE_PRICE}, ${
                replaceKey(i18n.translate("changeCurrencyLabel"), [currencyCode])
              } ${formatNumber(change, {
                positiveFormatPattern:
                  settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
              })}, ${
                i18n.translate("changePercentColon")
              } ${formatNumber(changePer, {
                positiveFormatPattern:
                  settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
              })}`;
          }
        },
      },
      series: series.map((data, i) => {
        let color = "";

        switch (data.id) {
          case SERIES_COLUM_TYPE.DECREASE:
            color = loadStyle("--chart-column-stack-decrease", "#C5221F").get();

            break;
          case SERIES_COLUM_TYPE.INCREASE:
            color = loadStyle("--chart-column-stack-increase", "#0F8080").get();

            break;
          default:
            color = loadStyle("--chart-column-stack-offset", "#35C0C0").get();
            break;
        }

        // When color is a pattern url(#...), Highcharts cannot apply
        // brightness to it and falls back to black on hover. Override the
        // hover state per-series to keep the original pattern fill.
        const isPatternColor = typeof color === 'string' && color.startsWith('url(');

        return {
          name: data.id,
          data: data.datas.map(item => item.value),
          pointWidth: loadStyle("--chart-column-width", undefined, "px").toNumber(),
          borderRadius: loadStyle("--chart-column-border-radius", 0, "px").toNumber(),
          borderWidth: loadStyle("--chart-column-border-width", 0, "px").toNumber(),
          groupPadding: loadStyle("--chart-column-group-padding", 0.2, "px").toNumber(),
          pointPadding: loadStyle("--chart-column-point-padding", 0.1, "px").toNumber(),
          maxPointWidth: loadStyle("--chart-column-max-width", 52, "px").toNumber(),
          color,
          ...(isPatternColor && {
            states: {
              hover: { color, brightness: 0 },
              select: { color, brightness: 0 },
            },
          }),
          id: data.id,
          dividendData: dividendData,
          sharePrice: sharePrice,
          currency: currencyCode,
          dataLabels: {
            enabled: false,
            align: "center",
            useHTML: true,
            formatter: function () {
              return number(this.y);
            },
            style: loadCommonStyle(chartUtils.chartEl, "--chart-column-labels"),
          },
        };
      }),
      tooltip: mergeObject(tooltipOptions(chartUtils), {
        formatter: function () {
          const { value, change, changePer, sharePrice, dividendReinvested, currency } = getTooltipValues({
            tooltipOpts: this,
            formatNumber,
          });
          const valueString = `
          <p class="custom-tooltip__item">
          <span class="custom-tooltip__label">${replaceKey(i18n.translate("valueCurrencyLabel"), [
            currency,
          ])}</span>
          <span class="custom-tooltip__value custom-tooltip__previous-value">${value}</span>
          <p>
          ${
            dividendReinvested
              ? `<p class="custom-tooltip__item">
          <span class="custom-tooltip__label">${replaceKey(i18n.translate("dividendReturnCurrencyLabel"), [
            currency,
          ])}</span>
          <span class="custom-tooltip__value">${dividendReinvested}</span>
          <p>`
              : ""
          }
          <p class="custom-tooltip__item">
          <span class="custom-tooltip__label">${replaceKey(i18n.translate("sharePriceCurrencyLabel"), [
            currency,
          ])}</span>
          <span class="custom-tooltip__value">${sharePrice}</span>
          <p>
          ${
            !!change
              ? `<p class="custom-tooltip__item">
          <span class="custom-tooltip__label">${replaceKey(i18n.translate("changeCurrencyLabel"), [
            currency,
          ])}</span>
          <span class="custom-tooltip__value ${
            change < 0 ? "custom-tooltip__down" : "custom-tooltip__up"
          } ">${formatNumber(change, {
                  positiveFormatPattern:
                    settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
                })}</span>
          <p>
          <p class="custom-tooltip__item">
          <span class="custom-tooltip__label">${i18n.translate("changePercentColon")}</span>
          <span class="custom-tooltip__value ${
            change < 0 ? "custom-tooltip__down" : "custom-tooltip__up"
          }">${formatNumber(changePer, {
                  positiveFormatPattern:
                    settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
                })}</span>
          <p>`
              : ""
          }`;
          return `<div class="custom-tooltip">
          <div class="custom-tooltip__time">${replaceKey(i18n.translate("valueOnLabel"), [
            formatDateTime(categories[this.x], settings),
          ])}</div>
          ${valueString}
        </div>
      `;
        },
      }),
    });

    return result;
  };

const createOptionsVictor = options => chartUtils => {
  const victorOption = createOptionsQuadrata(options)(chartUtils);
  const { settings, categories, formatNumber } = options;
  victorOption.tooltip = mergeObject(tooltipOptions(chartUtils), {
    formatter: function () {
      const { value, change, changePer, sharePrice, dividendReinvested, currency } = getTooltipValues({
        tooltipOpts: this,
        formatNumber,
      });
      const valueString = `
      <p class="custom-tooltip__item">
      <span class="custom-tooltip__label">${i18n.translate("value")}</span>
      <span class="custom-tooltip__value custom-tooltip__total">${value}</span>
      <span class="custom-tooltip__currency">(${currency})</span>

      <p>
      ${
        dividendReinvested
          ? `<p class="custom-tooltip__item">
      <span class="custom-tooltip__label">${i18n.translate("dividendsReinvested")}</span>
      <span class="custom-tooltip__value">${dividendReinvested}</span>
      <span class="custom-tooltip__currency">(${currency})</span>
      <p>`
          : ""
      }
      <p class="custom-tooltip__item">
      <span class="custom-tooltip__label">${i18n.translate("sharePrice")}</span>
      <span class="custom-tooltip__value">${sharePrice}</span>
      <span class="custom-tooltip__currency">(${currency})</span>
      <p>
      ${
        !!change
          ? `<p class="custom-tooltip__item">
      <span class="custom-tooltip__label">${i18n.translate("change")}</span>
      <span class="custom-tooltip__value ${
        change < 0 ? "custom-tooltip__down" : "custom-tooltip__up"
      } ">${formatNumber(change, {
              positiveFormatPattern: settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
            })}</span>
      <span class="custom-tooltip__currency">(${currency})</span>
      <p>
      <p class="custom-tooltip__item">
      <span class="custom-tooltip__label">${i18n.translate("change")}</span>
      <span class="custom-tooltip__value ${
        change < 0 ? "custom-tooltip__down" : "custom-tooltip__up"
      }">${formatNumber(changePer, {
              positiveFormatPattern: settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
            })}</span>
      <span class="custom-tooltip__currency">(%)</span>
      <p>`
          : ""
      }`;
      return `<div class="custom-tooltip">
      <div class="custom-tooltip__time">${formatDateTime(categories[this.x], settings)}</div>
      ${valueString}
    </div>
  `;
    },
  });
  return victorOption;
};
/**
 *
 * @param {{
 *  data: IAnnualClosePrice
 * }} param0
 * @returns
 */
export const HighChartsMultipleColumnVictor = memo(
  ({
    data,
    currencyCode,
    categories,
    settings,
    periodType,
    dividendData,
    sharePrice,
    formatNumber
  }) => {
    const chartRef = useRef();
    const labelSeriesNotes = {
      invested: i18n.translate("investedValue"),
      increase: i18n.translate("increaseValue"),
      decrease: i18n.translate("decreaseValue"),
    };
    const colorBlind = useSelector(colorBlindModeSelector);

    return (
      <div>
        <HighChartWrapper
          ref={chartRef}
          allowChartUpdate
          className="highcharts-multiple-column"
          createOptions={createOptionsVictor({
            data,
            currencyCode,
            categories,
            settings,
            periodType,
            dividendData,
            sharePrice,
            colorBlind,
            formatNumber,
          })}
          highCharts={Highcharts}
          isRtl={window.appSettings.isRtl}
          immutable={true}
        />
        <SeriesNoteColumnChart chartRef={chartRef} labels={labelSeriesNotes} />
      </div>
    );
  },
  (prev, next) => prev.data === next.data
);

/**
 *
 * @param {{
 *  data: IAnnualClosePrice
 * }} param0
 * @returns
 */
export const HighChartsMultipleColumnQuadrata = memo(
  ({
    data,
    currencyCode,
    categories,
    settings,
    periodType,
    dividendData,
    sharePrice,
    formatNumber
  }) => {
    const chartRef = useRef();
    const labelSeriesNotes = {
      invested: i18n.translate("previousPeriod"),
      increase: i18n.translate("increaseValue"),
      decrease: i18n.translate("decreaseValue"),
    };
    const colorBlind = useSelector(colorBlindModeSelector);

    return (
      <div className="quadrata-charts-column">
        <HighChartWrapper
          ref={chartRef}
          className="highcharts-multiple-column"
          highCharts={Highcharts}
          createOptions={createOptionsQuadrata({
            data,
            currencyCode: getCurrencyLabel(currencyCode),
            categories,
            settings,
            periodType,
            dividendData,
            sharePrice,
            colorBlind,
            formatNumber,
          })}
          isRtl={window.appSettings.isRtl}
          allowChartUpdate
          immutable={true}
        />
        <SeriesNoteColumnChart chartRef={chartRef} labels={labelSeriesNotes} />
      </div>
    );
  },
  (prev, next) => prev.data === next.data
);

export const HighChartsMultipleColumn = props => {
  const template = useTemplateSetting();

  switch (template) {
    case TOOL_TEMPLATE.victor:
      return <HighChartsMultipleColumnVictor {...props} />;
    case TOOL_TEMPLATE.quadrata:
      return <HighChartsMultipleColumnQuadrata {...props} />;
    default:
      throw new Error(`Template: ${template} do not exist.`);
  }
};
